<template>
  <!-- Explore by Theme section of homepage -->
  <div id="explore-theme-container">
    <!-- Loading indicator -->
    <a v-if="!loaded" name="explore-by-theme">
      <LoaderComponent />
    </a>
    <div v-else id="explore-theme-loader-wrapper">
      <div id="explore-theme">
        <div id="explore-theme-header">
          <a name="explore-by-theme">Explore by Theme</a>
        </div>
        <div
          id="explore-theme-description"
          v-html="exploreByThemeDescription"
        ></div>
        <!-- List of available Themes -->
        <div id="explore-theme-panels-wrapper">
          <div
            id="explore-theme-arrows-container"
            ref="themeArrowsContainer"
            v-if="themes.length > panels"
          >
            <button
              ref="themeLeftArrow"
              class="theme-arrow-button"
              @click="scrollRight()"
            >
              <font-awesome-icon
                :icon="['fas', 'chevron-circle-left']"
                class="theme-arrow"
              />
            </button>
            <button
              ref="themeRightArrow"
              class="theme-arrow-button"
              @click="scrollLeft()"
            >
              <font-awesome-icon
                :icon="['fas', 'chevron-circle-right']"
                class="theme-arrow"
              />
            </button>
          </div>
          <div id="explore-theme-panel-container" ref="themePanelContainer">
            <div
              class="explore-theme-panel"
              v-for="(theme, index) in themes"
              :key="theme.themeName"
              :class="`theme-${index + 1}`"
              ref="themes"
            >
              <div
                class="explore-theme-image"
                :style="{ backgroundImage: 'url(' + theme.image + ')' }"
              ></div>
              <!-- Optional "NEW" indicator for newly added themes -->
              <div class="theme-new-sticker" v-if="theme.newStatus">
                <div class="theme-new-triangle"></div>
                <div class="theme-new">NEW</div>
              </div>
              <div class="theme-name-description-container">
                <div class="theme-name">{{ theme.themeName }}</div>
                <div class="theme-description">
                  {{ theme.themeDescription }}
                </div>
              </div>
              <router-link
                :to="{
                  name: 'themes',
                  params: { theme: `t-${theme.themeId}` },
                }"
              >
                <button class="theme-button">Explore</button>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Randomly selected Themes -->
      <div id="highlighted-themes">
        <div id="highlighted-themes-header">Highlighted Themes</div>
        <div id="highlighted-theme-panel-container">
          <div
            class="highlighted-theme-panel"
            v-for="theme in highlighted"
            :key="theme.Id"
          >
            <div
              class="highlighted-theme-image"
              :style="{ backgroundImage: 'url(' + theme.image + ')' }"
            ></div>
            <div class="highlighted-theme-info">
              <router-link
                class="highlighted-info-link"
                :to="{
                  name: 'themes',
                  params: { theme: `t-${theme.themeId}` },
                }"
              >
              </router-link>
              <div class="highlighted-theme-name">
                {{ theme.themeName }}
              </div>
              <div class="highlighted-theme-description">
                {{ theme.themeDescription }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoaderComponent from "./LoaderComponent.vue";
import truncate from "../mixins/truncate.js";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
import homepageTexts from "../mixins/homepageTexts.js";
export default {
  name: "ExploreTheme",
  components: {
    LoaderComponent: LoaderComponent,
  },
  mixins: [truncate, language, media, homepageTexts],
  data() {
    return {
      exploreByThemeDescription: "",
      themes: [],
      highlighted: [],
      loaded: false,
      descriptionChars: 100,

      lessThan1199: window.matchMedia("(max-width: 1199px)").matches,
      lessThan599: window.matchMedia("(max-width: 599px)").matches,
    };
  },
  computed: {
    // Computed function for checking whether to show arrows
    panels: function () {
      if (this.lessThan1199) {
        return 3;
      }
      return 4;
    },
  },
  watch: {
    lessThan1199() {
      if (this.lessThan1199) {
        if (this.themes.length > 3) {
          this.$refs.themeLeftArrow.style.visibility = "hidden";
          this.$refs.themePanelContainer.addEventListener("scroll", (e) => {
            if (e.target.scrollLeft === 0) {
              this.$refs.themeLeftArrow.style.visibility = "hidden";
            } else {
              this.$refs.themeLeftArrow.style.visibility = "visible";
            }

            let fullWidth = this.$refs.themePanelContainer.scrollWidth;
            let viewWidth = this.$refs.themePanelContainer.clientWidth;
            let width = fullWidth - viewWidth;
            if (e.target.scrollLeft >= width) {
              this.$refs.themeRightArrow.style.visibility = "hidden";
            } else {
              this.$refs.themeRightArrow.style.visibility = "visible";
            }
          });
        }
      }
    },
  },
  methods: {
    getThemes: function () {
      axios({
        method: "GET",
        url: `${process.env.VUE_APP_API_ENDPOINT}/themes`,
      }).then(
        (result) => {
          let results = result.data;
          // Save relevant Theme information
          for (let i = 0; i < results.length; i++) {
            let theme = {};
            theme.newStatus = results[i].newStatus;
            theme.themeId = results[i].themeId;
            theme.themeName = this.returnValueInLanguage(
              "en",
              results[i].themeName
            ).value;
            if (results[i].themeDescription.length > 0) {
              theme.themeDescription = this.returnValueInLanguage(
                "en",
                results[i].themeDescription
              ).value;
            } else {
              theme.themeDescription = null;
            }
            // If description exists, truncate text to fit in panel
            if (theme.themeDescription) {
              theme.themeDescription = this.truncate(
                this.descriptionChars,
                theme.themeDescription
              );
            }
            theme.link = results[i].links.theme;
            let themePictures = results[i].themePictures;
            let random;
            if (themePictures.length > 0) {
              random =
                themePictures[Math.floor(Math.random() * themePictures.length)];
            } else {
              random = "";
            }
            theme.image = random.url;
            this.themes.push(theme);
            this.loaded = true;
          }
          this.$nextTick(() => {
            if (this.themes.length > 4) {
              this.$refs.themeLeftArrow.style.visibility = "hidden";
              this.$refs.themePanelContainer.addEventListener("scroll", (e) => {
                if (e.target.scrollLeft === 0) {
                  this.$refs.themeLeftArrow.style.visibility = "hidden";
                } else {
                  this.$refs.themeLeftArrow.style.visibility = "visible";
                }

                let fullWidth = this.$refs.themePanelContainer.scrollWidth;
                let viewWidth = this.$refs.themePanelContainer.clientWidth;
                let width = fullWidth - viewWidth;
                if (e.target.scrollLeft >= width) {
                  this.$refs.themeRightArrow.style.visibility = "hidden";
                } else {
                  this.$refs.themeRightArrow.style.visibility = "visible";
                }
              });
            }
          });
          // this.loaded = true;
          this.highlight(this.themes);
        },
        (error) => {
          console.error(error);
        }
      );
    },
    highlight: function (array) {
      let themes = array.slice();
      // Empty the highlighted array
      this.highlighted = [];
      // Shuffle the array
      // https://stackoverflow.com/a/962890
      let tmp,
        current,
        top = themes.length;
      if (top)
        while (--top) {
          current = Math.floor(Math.random() * (top + 1));
          tmp = themes[current];
          themes[current] = themes[top];
          themes[top] = tmp;
        }
      // Push the first two "random" Themes into the highlighted array
      this.highlighted.push(themes[0]);
      this.highlighted.push(themes[1]);
    },
    scrollLeft: function () {
      let panelWidth = this.$refs.themes[0].clientWidth + 20;
      let current = this.$refs.themePanelContainer.scrollLeft;
      this.$refs.themePanelContainer.scrollTo({
        left: current + panelWidth,
        behavior: "smooth",
      });
    },
    scrollRight: function () {
      let panelWidth = this.$refs.themes[0].clientWidth + 20;
      let current = this.$refs.themePanelContainer.scrollLeft;
      this.$refs.themePanelContainer.scrollTo({
        left: current - panelWidth,
        behavior: "smooth",
      });
    },
  },
  mounted() {
    this.getTexts("exploreByThemeDescription", "Home-Explore-by-Theme");
    this.getThemes();
  },
};
</script>

<style scoped lang="scss">
.loader-container {
  height: 1200px;
}

#explore-theme-container {
  width: 100%;
  padding: 0 40px;
}

#explore-theme-header {
  @include headings;
  @include font-size-1;
  margin-bottom: 30px;
}
#explore-theme-header a {
  cursor: default;
}
#explore-theme-header a:hover {
  text-decoration: none;
}

#explore-theme-description {
  @include texts;
  @include font-size-4;
  color: $medium-dark-grey;
  margin-bottom: 22px;
}

#explore-theme-panels-wrapper {
  position: relative;
  width: 100%;
}

#explore-theme-arrows-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  height: 600px;
  width: 100%;
}
.theme-arrow-button {
  @include button-styles;
  margin: 10px;
  z-index: 1;
}
.theme-arrow-button:focus {
  outline: none;
}
.theme-arrow {
  height: 50px;
  width: 50px;
  color: rgba(white, 0.6);
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #explore-theme-arrows-container {
    display: none;
  }
}

#explore-theme-panel-container {
  display: flex;
  position: relative;
  width: 100%;
  margin-bottom: 30px;
  overflow: auto;
  scrollbar-width: none; /* Firefox */
}
/* Chrome */
#explore-theme-panel-container::-webkit-scrollbar {
  height: 0;
  width: 0;
}

.explore-theme-panel {
  @include flex-center;
  flex-direction: column;
  justify-content: space-between;
  height: 600px;
  // Width accounting for margin (to fill container)
  min-width: calc(25% - 15px);
  width: calc(25% - 15px);
  position: relative;
  color: $white;
  text-align: center;
  padding: 100px 20px;
  margin-left: 20px;
  overflow: hidden;
  z-index: 0;
}
.theme-1 {
  margin-left: 0;
}
.explore-theme-image {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-position: center center;
  background-size: cover;
  transition: all 0.25s;
  z-index: -1;
}
.explore-theme-panel:hover .explore-theme-image {
  transform: scale(1.2);
}
.explore-theme-image::after {
  content: "";
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  box-shadow: inset 0 0 0 300px rgba($dark-grey, 0.45),
    0 2px 8px 2px $light-grey;
}
.theme-new-sticker {
  height: 100px;
  width: 100px;
  position: absolute;
  top: 0;
  right: 0;
}
.theme-new-triangle {
  position: absolute;
  top: 0;
  right: 0;
  height: 0;
  width: 0;
  border-top: 100px solid $red;
  border-left: 100px solid transparent;
}
.theme-new {
  @include flex-center;
  @include headings;
  @include font-size-5;
  height: 50px;
  width: 50px;
  position: absolute;
  top: 0;
  right: -15%;
  transform: rotate(45deg);
  transform-origin: top left;
  color: $white;
}
.theme-name-description-container {
  height: 100%;
  width: 100%;
}
.theme-name {
  @include header-font;
  @include font-size-3;
  min-height: 140px;
  font-weight: bold;
}
.theme-description {
  @include texts;
  @include font-size-5;
  color: $white;
}
.theme-button {
  @include primary-button;
  height: 40px;
  width: 200px;
}

#highlighted-themes {
  margin-bottom: 40px;
}
#highlighted-themes-header {
  @include headings;
  @include font-size-2;
  margin-bottom: 20px;
}
#highlighted-theme-panel-container {
  @include flex-center;
}
.highlighted-theme-panel {
  display: flex;
  align-items: flex-end;
  height: 400px;
  width: 50%;
  position: relative;
  box-shadow: 0 2px 8px 2px $light-grey;
  margin-left: 20px;
  overflow: hidden;
  z-index: 0;
}
.highlighted-theme-panel:first-child {
  margin-left: 0;
}
.highlighted-theme-image {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  background-position: center;
  background-size: cover;
  transition: all 0.25s;
  z-index: -1;
}
.highlighted-theme-panel:hover .highlighted-theme-image {
  transform: scale(1.2);
}
.highlighted-theme-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  height: 125px;
  width: 100%;
  color: white;
  background-color: rgba($dark-grey, 0.5);
  padding: 20px 30px;
}
.highlighted-info-link {
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  color: white;
}
.highlighted-theme-name {
  @include header-font;
  @include font-size-4;
  font-weight: bold;
  margin-bottom: 15px;
}
.highlighted-theme-description {
  @include body-font;
  @include font-size-7;
}

@media only screen and (max-width: 1199px) {
  #explore-theme-header {
    @include headings;
    @include font-size-3;
  }
  #explore-theme-description {
    @include texts;
    @include font-size-5;
    color: $medium-dark-grey;
  }
  .explore-theme-panel {
    min-width: calc(33.3% - 15px);
    width: calc(33.3% - 15px);
  }
  .theme-name {
    @include font-size-4;
  }
  #highlighted-themes-header {
    @include headings;
    @include font-size-3;
  }
}

@media only screen and (max-width: 949px) {
  .explore-theme-panel {
    min-width: calc(50% - 15px);
    width: calc(50% - 15px);
  }
  #highlighted-theme-panel-container {
    flex-direction: column;
  }
  .highlighted-theme-panel {
    width: 100%;
    margin-left: 0;
    margin-top: 20px;
  }
  .highlighted-theme-panel:first-child {
    margin-top: 0;
  }
}

@media only screen and (max-width: 599px) {
  #explore-theme-arrows-container {
    height: 450px;
  }
  .theme-arrow {
    height: 40px;
    width: 40px;
  }
  .explore-theme-panel {
    height: 450px;
    min-width: 100%;
    width: 100%;
    padding: 50px 20px;
  }
  .highlighted-theme-panel {
    height: 325px;
  }
}

@media only screen and (max-width: 499px) {
  #explore-theme-header,
  #highlighted-themes-header {
    @include font-size-4;
    margin-bottom: 20px;
  }
  #explore-theme-description {
    @include font-size-6;
  }
  .theme-new-sticker {
    height: 75px;
    width: 75px;
  }
  .theme-new-triangle {
    border-top: 75px solid $red;
    border-left: 75px solid transparent;
  }
  .theme-new {
    @include font-size-7;
    height: 40px;
    width: 40px;
  }
  .theme-description {
    @include font-size-6;
  }
  .highlighted-theme-panel {
    height: 275px;
  }
  .highlighted-theme-info {
    height: 90px;
    padding: 15px 20px;
  }
  .highlighted-theme-name {
    @include font-size-5;
    margin-bottom: 0;
  }
  .highlighted-theme-description {
    display: none;
  }
}
</style>
